import type { FactorEntry } from "../services/productFactorsService";
import { factorsToDailyReturns } from "./riskMetrics";
import {
  buildWeightedDraftStrategyDailyBreakdowns,
  type DraftFactorProductInput,
} from "./strategyAnalyzerDraftFactors";

/**
 * Plain-English bucket for the focus day's move RELATIVE TO THE BENCHMARK, keyed to
 * magnitude (|sigma|) not direction. We are not interested in vanilla (market-
 * driven) moves: the signal is the active return (strategy - benchmark) measured
 * against the strategy's typical tracking error.
 */
export type MoveBand = "quiet" | "normal" | "elevated" | "large" | "insufficient" | "flat";

export interface MoveContext {
  strategyDayPct: number | null; // focus day's reconstructed strategy move (%)
  benchmarkDayPct: number | null; // focus day's benchmark move (%)
  activeDayPct: number | null; // strategyDay - benchmarkDay (%)
  trackingErrorPct: number | null; // daily stdev of active returns (%), full overlap
  sigma: number | null; // activeDayPct / trackingErrorPct
  bandLowPct: number | null; // -1 tracking error (typical active low)
  bandHighPct: number | null; // +1 tracking error (typical active high)
  percentile: number | null; // 0..100: share of other days with |active| <= |focus|
  sampleDays: number; // overlapping baseline days (excludes today and the focus day)
  band: MoveBand;
  activeReturnsPct: number[]; // baseline active returns (%), for the curve
  activeReturns: BaselineDay[]; // baseline active returns with dates, for the expanded view
}

export interface ReconstructedDay {
  date: string; // UTC day key "YYYY-MM-DD"
  strategyPct: number; // reconstructed strategy return (%)
  benchmarkPct: number | null; // benchmark return (%) on the same day, if present
}

export interface BaselineDay {
  date: string; // UTC day key "YYYY-MM-DD"
  activePct: number; // active return (strategy - benchmark) (%)
}

// Mirrors riskMetrics.MIN_DATA_POINTS so the teaser and the analyzer agree on
// what counts as "enough history".
const MIN_SAMPLE = 10;

function sampleStdDev(values: number[]): number {
  if (values.length < 2) return 0;
  const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
  const variance =
    values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / (values.length - 1);
  return Math.sqrt(variance);
}

export function currentUtcDayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function finiteOrNull(value: number | null): number | null {
  return value != null && Number.isFinite(value) ? value : null;
}

/**
 * Core math: contextualize the focus day's active return against the distribution
 * of the baseline active returns. Pure -- callers supply the already-aligned
 * baseline series plus the focus day's strategy and benchmark moves.
 */
export function computeMoveContext(
  baselineActivePct: number[],
  focusStrategyPct: number | null,
  focusBenchmarkPct: number | null
): MoveContext {
  const sampleDays = baselineActivePct.length;
  const strategyDayPct = finiteOrNull(focusStrategyPct);
  const benchmarkDayPct = finiteOrNull(focusBenchmarkPct);
  const activeDayPct =
    strategyDayPct != null && benchmarkDayPct != null
      ? strategyDayPct - benchmarkDayPct
      : null;

  const base: MoveContext = {
    strategyDayPct,
    benchmarkDayPct,
    activeDayPct,
    trackingErrorPct: null,
    sigma: null,
    bandLowPct: null,
    bandHighPct: null,
    percentile: null,
    sampleDays,
    band: "insufficient",
    activeReturnsPct: baselineActivePct,
    activeReturns: [],
  };

  if (sampleDays < MIN_SAMPLE) return base;

  const trackingErrorPct = sampleStdDev(baselineActivePct);
  if (!(trackingErrorPct > 0)) {
    return { ...base, trackingErrorPct: 0, band: "flat" };
  }

  const bandLowPct = -trackingErrorPct;
  const bandHighPct = trackingErrorPct;

  // Baseline is sufficient (we can show the typical active range), but there is no
  // comparable move for the focus day (missing strategy or benchmark data).
  if (activeDayPct == null) {
    return { ...base, trackingErrorPct, bandLowPct, bandHighPct, band: "normal" };
  }

  const sigma = activeDayPct / trackingErrorPct;
  const absActive = Math.abs(activeDayPct);
  const within = baselineActivePct.filter((r) => Math.abs(r) <= absActive).length;
  const percentile = (within / sampleDays) * 100;

  const absSigma = Math.abs(sigma);
  const band: MoveBand =
    absSigma < 0.5 ? "quiet" : absSigma < 1 ? "normal" : absSigma < 2 ? "elevated" : "large";

  return {
    ...base,
    trackingErrorPct,
    sigma,
    bandLowPct,
    bandHighPct,
    percentile,
    band,
  };
}

/**
 * Reconstructs the CURRENT strategy as a backtest from its constituents at the
 * current signed weights and pairs each day with the benchmark return. Pure: no
 * fetching. Days are sorted ascending.
 *
 * Uses "rebalanced" mode -- the passed weights are re-applied every day, so the
 * series characterizes a FIXED mix over history. The hook seeds it with the
 * customer's CURRENT drifted weights (their actual exposure today), so this answers
 * "for the mix you hold right now, what does a normal day look like?".
 *
 * Why not drifted mode: the platform never rebalances, so a customer's weights drift
 * from their (often recent) start date. Drifted mode would instead re-derive a drift
 * path from the oldest common date using target weights -- inventing weights the
 * customer never held. We already know today's real weights (driftedWeight), so we
 * anchor on those and hold them fixed: path-independent and start-agnostic.
 *
 * Using the reconstructed current strategy -- not the realized historical series --
 * is required because the realized series bakes in past weight changes; if the
 * strategy was edited, its old volatility is irrelevant to the current strategy.
 */
export function reconstructDailyActiveSeries(
  strategyProducts: DraftFactorProductInput[],
  benchmarkFactors: FactorEntry[]
): ReconstructedDay[] {
  const daily = buildWeightedDraftStrategyDailyBreakdowns(strategyProducts, "rebalanced");
  const benchmarkByDay = new Map(
    factorsToDailyReturns(benchmarkFactors).map((entry) => [entry.t.slice(0, 10), entry.r * 100])
  );
  return daily.map((day) => ({
    date: day.date,
    strategyPct: day.strategyReturnPct,
    benchmarkPct: benchmarkByDay.get(day.date.slice(0, 10)) ?? null,
  }));
}

/**
 * Computes the move context for one focus day (defaults to the current UTC day).
 * The baseline excludes both the in-progress current UTC day (a partial session)
 * and the focus day itself (a value must not sit in the distribution it is judged
 * against).
 */
export function computeMoveContextForDays(
  days: ReconstructedDay[],
  focusDayKey?: string
): MoveContext {
  const todayKey = currentUtcDayKey();
  const focusKey = focusDayKey ?? todayKey;

  const baseline: BaselineDay[] = [];
  let focusStrategyPct: number | null = null;
  let focusBenchmarkPct: number | null = null;

  for (const day of days) {
    if (day.date === focusKey) {
      focusStrategyPct = day.strategyPct;
      focusBenchmarkPct = day.benchmarkPct;
      continue;
    }
    // Today is partial -> never part of the baseline (unless it is the focus day,
    // handled above).
    if (day.date === todayKey) continue;
    // Active return needs both legs on the same day.
    if (day.benchmarkPct == null) continue;
    baseline.push({ date: day.date, activePct: day.strategyPct - day.benchmarkPct });
  }

  const ctx = computeMoveContext(
    baseline.map((b) => b.activePct),
    focusStrategyPct,
    focusBenchmarkPct
  );
  return { ...ctx, activeReturns: baseline };
}

/**
 * The K baseline days whose active return is closest to the focus day's gap -- the
 * "nearest neighbours" that show today is not unique. Pure.
 */
export function nearestActiveDays(
  activeReturns: BaselineDay[],
  focusActivePct: number,
  k: number = 5
): BaselineDay[] {
  return [...activeReturns]
    .sort((a, b) => Math.abs(a.activePct - focusActivePct) - Math.abs(b.activePct - focusActivePct))
    .slice(0, k);
}

/**
 * Convenience wrapper: reconstruct + compute for a single focus day.
 */
export function buildMoveContextFromSeries(
  strategyProducts: DraftFactorProductInput[],
  benchmarkFactors: FactorEntry[],
  focusDayKey?: string
): MoveContext {
  return computeMoveContextForDays(
    reconstructDailyActiveSeries(strategyProducts, benchmarkFactors),
    focusDayKey
  );
}
